import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, BarChart3, Users, FileText, Activity, TrendingUp, Mail, ChevronDown, ChevronUp, Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [showAllLeads, setShowAllLeads] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if already authenticated
    const token = sessionStorage.getItem("admin_token");
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const res = await axios.post(`${API}/admin/verify?token=${token}`);
      if (res.data.valid) {
        setIsAuthenticated(true);
        loadDashboardData();
      } else {
        sessionStorage.removeItem("admin_token");
        setLoading(false);
      }
    } catch {
      sessionStorage.removeItem("admin_token");
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.success) {
        sessionStorage.setItem("admin_token", res.data.token);
        setIsAuthenticated(true);
        loadDashboardData();
        toast.success("Welcome back!");
      } else {
        toast.error("Invalid credentials");
      }
    } catch {
      toast.error("Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setIsAuthenticated(false);
    setAnalytics(null);
    setLeads(null);
    setFunnel(null);
    toast.success("Logged out");
  };

  const loadDashboardData = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/analytics`),
      axios.get(`${API}/admin/leads`),
      axios.get(`${API}/admin/funnel`),
    ]).then(([a, l, f]) => {
      setAnalytics(a.data); setLeads(l.data); setFunnel(f.data);
    }).catch(console.error).finally(() => setLoading(false));
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <Card data-testid="admin-login-card" className="w-full max-w-sm rounded-2xl bg-[#232134] border-[#33304a]">
          <CardContent className="p-6">
            <div className="flex flex-col items-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#e91e63]/10 flex items-center justify-center mb-4">
                <Lock className="w-7 h-7 text-[#e91e63]" />
              </div>
              <h1 className="text-xl font-semibold text-white">Admin Access</h1>
              <p className="text-sm text-[#7a7894] mt-1">SmileSense Dashboard</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  data-testid="admin-username-input"
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-12 rounded-xl bg-[#15132a] border-[#33304a] text-white placeholder:text-[#5a5873]"
                  required
                />
              </div>
              <div>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl bg-[#15132a] border-[#33304a] text-white placeholder:text-[#5a5873]"
                  required
                />
              </div>
              <Button
                data-testid="admin-login-btn"
                type="submit"
                disabled={loginLoading}
                className="btn-gradient w-full h-12 text-base font-semibold rounded-xl text-white border-0"
              >
                {loginLoading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="w-full mt-4 text-[#7a7894] hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to App
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="processing-ring" />
    </div>
  );

  const maxDaily = Math.max(...(analytics?.daily?.map(d => d.count) || [1]), 1);
  const funnelMax = Math.max(...(funnel?.funnel?.map(f => f.count) || [1]), 1);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pb-8">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#1a182a]/90 backdrop-blur-lg border-b border-[#33304a]">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-5 py-4">
          <Button data-testid="admin-back-btn" variant="ghost" size="icon" onClick={() => navigate("/")}
            className="rounded-xl text-[#7a7894] hover:text-white hover:bg-[#2a273b]">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white">Admin Dashboard</h1>
            <p className="text-xs text-[#5a5873]">SmileSense Analytics</p>
          </div>
          <Badge className="bg-[#e91e63]/10 text-[#e91e63] border border-[#e91e63]/20 text-xs rounded-full mr-2">
            <Activity className="w-3 h-3 mr-1" /> Live
          </Badge>
          <Button
            data-testid="admin-logout-btn"
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="rounded-xl text-[#7a7894] hover:text-[#e91e63] hover:bg-[#2a273b]"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 mt-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Total Scans", value: analytics?.total_scans || 0, icon: BarChart3, color: "#e91e63" },
            { label: "Lab Exports", value: analytics?.total_exports || 0, icon: FileText, color: "#2196f3" },
            { label: "Leads", value: analytics?.total_leads || 0, icon: Users, color: "#ff9f1c" },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}
              className="rounded-2xl bg-[#232134] border-[#33304a]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "15" }}>
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                </div>
                <div className="text-2xl font-bold text-white">{value.toLocaleString()}</div>
                <div className="text-xs text-[#7a7894] mt-1">{label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Daily Scans Chart */}
        <Card data-testid="daily-scans-chart" className="rounded-2xl bg-[#232134] border-[#33304a]">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-[#e91e63]" />
              <span className="text-sm font-medium text-white">Daily Scans (14 days)</span>
            </div>
            <div className="flex items-end gap-1.5 h-40">
              {analytics?.daily?.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="w-full relative flex items-end h-32">
                    <div className="w-full rounded-t-md transition-all group-hover:opacity-80"
                      style={{
                        height: `${Math.max(4, (day.count / maxDaily) * 100)}%`,
                        background: day.count > 0 ? "linear-gradient(to top, #e91e63, #ff9f1c)" : "#33304a",
                      }}>
                      {day.count > 0 && (
                        <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-mono text-[#e91e63] opacity-0 group-hover:opacity-100 transition-opacity">
                          {day.count}
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="text-[9px] text-[#5a5873] truncate w-full text-center">{day.label?.slice(0, 6)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Weekly Summary */}
        <Card data-testid="weekly-summary" className="rounded-2xl bg-[#232134] border-[#33304a]">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-[#2196f3]" />
              <span className="text-sm font-medium text-white">Weekly Summary</span>
            </div>
            <div className="space-y-3">
              {analytics?.weekly?.map((week, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-[#7a7894] w-16 flex-shrink-0">{week.label}</span>
                  <div className="flex-1 h-6 rounded-full bg-[#15132a] overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.max(2, (week.count / Math.max(...analytics.weekly.map(w => w.count), 1)) * 100)}%`,
                        background: "linear-gradient(90deg, #2196f3, #e91e63)"
                      }} />
                  </div>
                  <span className="text-sm font-mono text-white w-8 text-right">{week.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* User Journey Funnel */}
        <Card data-testid="user-funnel" className="rounded-2xl bg-[#232134] border-[#33304a]">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-[#ff9f1c]" />
              <span className="text-sm font-medium text-white">User Journey Funnel</span>
            </div>
            <div className="space-y-2">
              {funnel?.funnel?.map((step, i) => {
                const pct = funnelMax > 0 ? (step.count / funnelMax) * 100 : 0;
                const colors = ["#ff9f1c", "#e91e63", "#2196f3", "#22FF66", "#A855F7"];
                const labels = { landing: "Landing Page", capture: "Image Capture", analyze: "Shade Analysis", results: "View Results", export: "Lab Slip Export" };
                return (
                  <div key={i} data-testid={`funnel-step-${step.step}`} className="flex items-center gap-3">
                    <span className="text-xs text-[#7a7894] w-24 flex-shrink-0 capitalize">{labels[step.step] || step.step}</span>
                    <div className="flex-1 h-8 rounded-lg bg-[#15132a] overflow-hidden relative">
                      <div className="h-full rounded-lg transition-all flex items-center px-3"
                        style={{ width: `${Math.max(8, pct)}%`, backgroundColor: colors[i % colors.length] + "30" }}>
                        <div className="h-full rounded-lg" style={{ width: "100%", backgroundColor: colors[i % colors.length], opacity: 0.6 }} />
                      </div>
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-white">{step.count}</span>
                    </div>
                    {i > 0 && funnel.funnel[i - 1].count > 0 && (
                      <span className="text-[10px] text-[#5a5873] w-10 text-right">
                        {Math.round((step.count / funnel.funnel[i - 1].count) * 100)}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card data-testid="leads-table" className="rounded-2xl bg-[#232134] border-[#33304a]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#ff9f1c]" />
                <span className="text-sm font-medium text-white">Captured Leads ({leads?.total || 0})</span>
              </div>
              {leads?.leads?.length > 5 && (
                <button onClick={() => setShowAllLeads(!showAllLeads)} className="text-xs text-[#e91e63] hover:text-[#ff9f1c] transition-colors flex items-center gap-1">
                  {showAllLeads ? "Show less" : "Show all"}
                  {showAllLeads ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
            </div>
            {leads?.leads?.length > 0 ? (
              <div className="space-y-2">
                {(showAllLeads ? leads.leads : leads.leads.slice(0, 5)).map((lead, i) => (
                  <div key={i} data-testid={`lead-row-${i}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-[#15132a] border border-[#33304a]/50">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ff9f1c] to-[#e91e63] flex items-center justify-center text-white text-xs font-bold">
                      {(lead.name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{lead.name || "Unknown"}</p>
                      <p className="text-xs text-[#7a7894] truncate">{lead.email}</p>
                    </div>
                    {lead.clinic_name && <span className="text-xs text-[#5a5873] hidden sm:block">{lead.clinic_name}</span>}
                    <Badge className="text-[10px] bg-[#2196f3]/10 text-[#2196f3] border-[#2196f3]/20 rounded-full">
                      {lead.export_count || 1}x
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#5a5873] text-center py-8">No leads captured yet. Leads are collected when users export lab slips.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
